import { useEffect, useState } from "react";
import { getDataClient } from "@/lib/dataClient";
import { useDataQuery, getCurrentUserId } from "@/hooks/useDataQuery";

// AI Video Ders (HeyGen) — senaryodan avatar öğretmen videosu üretimi.
// Üretim işleri ai_video_jobs tablosunda tutulur; gerçek üretim
// "ai-video" edge function üzerinden HeyGen API ile yapılır.

export interface AiVideoJob {
  id: string;
  title: string;
  subject: string;
  script: string;
  avatar_id: string;
  voice_id: string;
  status: string;
  video_url: string;
  thumbnail: string;
  duration_minutes: number;
  error_message: string;
  created_at: string;
}

export interface AiAvatarOption {
  id: string;
  name: string;
  gender: string;
  preview: string;
}

export interface AiVoiceOption {
  id: string;
  name: string;
  language: string;
  gender: string;
}

// API anahtarı tanımlı değilken gösterilecek yedek avatar/ses seçenekleri.
// Anahtar girildiğinde "options" aksiyonu HeyGen'den gerçek listeyi çeker.
export const AVATAR_PRESETS: AiAvatarOption[] = [
  { id: "Anna_public_3_20240108", name: "Anna (Kadın)", gender: "Kadın", preview: "" },
  { id: "Bryan_public_3_20240108", name: "Bryan (Erkek)", gender: "Erkek", preview: "" },
  { id: "Susan_public_3_20240108", name: "Susan (Kadın)", gender: "Kadın", preview: "" },
  { id: "Eric_public_3_20240108", name: "Eric (Erkek)", gender: "Erkek", preview: "" },
  { id: "Daisy_public_3_20240108", name: "Daisy (Kadın)", gender: "Kadın", preview: "" },
  { id: "Tyler_public_3_20240108", name: "Tyler (Erkek)", gender: "Erkek", preview: "" },
];

export const VOICE_PRESETS: AiVoiceOption[] = [
  { id: "1bd001e7e50f421d891986aad5158bc8", name: "Varsayılan Ses", language: "Türkçe", gender: "Erkek" },
  { id: "0c0d7d1b8ce84446a5481c4a3e56f7d0", name: "Türkçe (Kadın)", language: "Türkçe", gender: "Kadın" },
];

function mapJob(row: {
  id: string;
  title: string;
  subject: string;
  script: string;
  avatar_id: string | null;
  voice_id: string | null;
  status: string;
  video_url: string | null;
  thumbnail: string | null;
  duration_minutes: number | null;
  error_message: string | null;
  created_at: string;
}): AiVideoJob {
  return {
    id: row.id,
    title: row.title,
    subject: row.subject,
    script: row.script,
    avatar_id: row.avatar_id ?? "",
    voice_id: row.voice_id ?? "",
    status: row.status,
    video_url: row.video_url ?? "",
    thumbnail: row.thumbnail ?? "",
    duration_minutes: row.duration_minutes ?? 0,
    error_message: row.error_message ?? "",
    created_at: row.created_at,
  };
}

export interface AiVideoOptions {
  configured: boolean;
  avatars: AiAvatarOption[];
  voices: AiVoiceOption[];
}

export function useAiVideoOptions() {
  const [options, setOptions] = useState<AiVideoOptions>({
    configured: false,
    avatars: AVATAR_PRESETS,
    voices: VOICE_PRESETS,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const dataClient = getDataClient();
        const { data, error } = await dataClient.functions.invoke("ai-video", {
          body: { action: "options" },
        });
        if (cancelled) return;
        if (!error && data) {
          setOptions({
            configured: Boolean(data.configured),
            avatars: Array.isArray(data.avatars) && data.avatars.length
              ? (data.avatars as AiAvatarOption[])
              : AVATAR_PRESETS,
            voices: Array.isArray(data.voices) && data.voices.length
              ? (data.voices as AiVoiceOption[])
              : VOICE_PRESETS,
          });
        }
      } catch {
        // Anahtar yoksa yedek seçenekler kalır.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { options, loading };
}

export function useAiVideoJobs() {
  const query = useDataQuery<AiVideoJob[]>(async () => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return [];
    const { data, error } = await dataClient
      .from("ai_video_jobs")
      .select("*")
      .eq("teacher_id", userId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapJob);
  });

  const createJob = async (input: {
    title: string;
    subject: string;
    script: string;
    avatar_id: string;
    voice_id: string;
  }) => {
    const dataClient = getDataClient();
    const { data, error } = await dataClient.functions.invoke("ai-video", {
      body: { action: "create", ...input },
    });
    if (error) throw new Error(error.message || "Üretim başlatılamadı.");
    if (data && typeof data.error === "string") {
      throw new Error(data.error);
    }
    query.reload();
    return data;
  };

  const checkStatus = async () => {
    const dataClient = getDataClient();
    const { data, error } = await dataClient.functions.invoke("ai-video", {
      body: { action: "check" },
    });
    if (error) throw new Error(error.message || "Durum sorgulanamadı.");
    if (data && typeof data.error === "string") {
      throw new Error(data.error);
    }
    query.reload();
    return data;
  };

  return { ...query, createJob, checkStatus };
}